import React, { useState, useEffect } from 'react';
import { MessageCircle } from 'lucide-react';
import { DEFAULT_SETTINGS, subscribeAdminSettings } from '../utils/adminSettings';

const ThingsNotePage = ({ isMobile }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  
  useEffect(() => {
    const unsub = subscribeAdminSettings(setSettings); // 실시간으로 반영
    return () => unsub();
  }, []);

  // 카카오톡 오픈채팅 링크 클릭 핸들러
  const handleKakaoClick = () => {
    if (settings.kakaoOpenChatUrl) {
      window.open(settings.kakaoOpenChatUrl, '_blank');
    }
  };

  // 카카오톡 아이콘 컴포넌트 (클릭 가능한 경우와 아닌 경우)
  const KakaoIcon = ({ size = 24, clickable = false }) => {
    const iconStyle = {
      width: `${size}px`,
      height: `${size}px`,
      backgroundColor: '#FEE500',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: clickable && settings.kakaoOpenChatUrl ? 'pointer' : 'default',
      opacity: clickable && !settings.kakaoOpenChatUrl ? 0.3 : 1,
      transition: 'all 0.2s ease',
      boxShadow: clickable ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
    };

    const hoverStyle = clickable && settings.kakaoOpenChatUrl ? {
      transform: 'scale(1.05)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
    } : {};

    return (
      <div 
        style={iconStyle}
        onClick={clickable ? handleKakaoClick : undefined}
        onMouseEnter={clickable ? (e) => Object.assign(e.target.style, hoverStyle) : undefined}
        onMouseLeave={clickable ? (e) => Object.assign(e.target.style, iconStyle) : undefined}
        title={clickable && settings.kakaoOpenChatUrl ? '카카오톡 오픈채팅 참여하기' : ''}
      >
        <MessageCircle size={size * 0.6} color="#3C1E1E" />
      </div>
    );
  };
  
  // 모바일 버전 렌더링 - 향상된 디자인
  if (isMobile) {
    return (
      <div style={{
        width: '100%',
        backgroundColor: '#000',
        overflow: 'hidden',
        minHeight: '100vh',
      }}>
        <div style={{
          width: '100%',
          position: 'relative',
          color: '#ffffff',
          fontFamily: 'Pretendard, sans-serif',
          padding: '20px 15px 60px 15px',
        }}>
          {/* 중앙 "Things Note:" 제목 - 모바일 */}
          <div style={{
            marginTop: '20px',
            marginBottom: '10px',
            fontSize: '48px',
            fontWeight: 'bold',
            lineHeight: '1',
            background: 'linear-gradient(to right, #ffffff, #aaaaaa)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 10px rgba(255,255,255,0.2)',
            textAlign: 'center',
          }}>
            Things Note:
          </div>

          {/* 카드형 정보 영역 - 모바일 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
            marginTop: '20px',
          }}>
            {/* 카드 1 - 장비장 연락망 */}
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '10px',
              padding: '16px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
              borderLeft: '3px solid #ff9500',
            }}>
              <div style={{ 
                fontWeight: '600', 
                marginBottom: '8px', 
                color: '#ff9500',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <span>장비장 연락망</span>
                {settings.kakaoOpenChatUrl && (
                  <KakaoIcon size={24} clickable={true} />
                )}
              </div>
              <div style={{ 
                lineHeight: '1.5', 
                fontWeight: '300',
                fontSize: '14px',
              }}>
                {settings.adminName}<br/>{settings.adminPhone}
              </div>
            </div>

            {settings.notesCards.map((card, idx) => (
              <div key={idx} style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '10px',
                padding: '16px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                borderLeft: `3px solid ${card.color}`,
              }}>
                <div style={{
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: card.color,
                  fontSize: '16px',
                }}>{card.title}</div>
                <div style={{
                  lineHeight: '1.5',
                  fontWeight: '300',
                  fontSize: '14px',
                  whiteSpace: 'pre-line'
                }}>
                  {card.content}
                </div>
              </div>
            ))}
          </div>

          {/* 배경 이미지 제거됨 */}
        </div>
      </div>
    );
  }

  // 데스크탑 버전 렌더링 (원본 디자인 유지)
  return (
    <div style={{
      width: '100vw',
      height: '910px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#FFFFFF',
      overflow: 'hidden', // 잘리게 처리
    }}>
      <div style={{
        width: '1440px',
        height: '1080px',
        position: 'relative',
        backgroundColor: '#000',
        color: '#ffffff',
        fontFamily: 'Pretendard, sans-serif',
      }}>
        {/* 상단 정보영역 */}
        <div style={{
          position: 'absolute',
          top: '100px',
          left: '60px',
          right: '60px',
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '16px',
          paddingBottom: '20px',
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        }}>
          <div style={{
            position: 'absolute',
            width: '100%',
            height: '1px',
            backgroundColor: 'white',
            top: '25px'
          }}></div>

          <div>
            <div style={{ 
              fontWeight: '200', 
              marginBottom: '5px', 
              position: 'absolute', 
              left: '2px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              장비장 연락망
              {settings.kakaoOpenChatUrl && (
                <KakaoIcon size={20} clickable={true} />
              )}
            </div>
            <div style={{ lineHeight: '1.2', position: 'absolute', left: '2px', top: '30px', fontWeight: '200', }}>
              {settings.adminName}<br/>{settings.adminPhone}
            </div>
          </div>
          <div style={{
            fontWeight: '200',
            width: '0px',
            height: '4px',
            backgroundColor: 'white',
            margin: '0 0px'
          }} />
          <div>
            <div style={{ fontWeight: '200', position: 'absolute', left: '300px' }}>
              {settings.notesCards[0]?.title ?? '대여일'}
            </div>
            <div style={{ lineHeight: '1.2', position: 'absolute', left: '300px', top: '30px', fontWeight: '200', whiteSpace: 'pre-line' }}>
              {settings.notesCards[0]?.content ?? ''}
            </div>
          </div>
          <div style={{ width: '0px', height: '60px', backgroundColor: 'white', margin: '0 20px' }} />
          <div>
            <div style={{ fontWeight: '200', position: 'absolute', left: '650px' }}>
              {settings.notesCards[1]?.title ?? '반납시'}
            </div>
            <div style={{ lineHeight: '1.2', fontWeight: '200', position: 'absolute', left: '650px', top: '30px', whiteSpace: 'pre-line' }}>
              {settings.notesCards[1]?.content ?? ''}
            </div>
          </div>
          <div style={{ width: '0px', height: '60px', backgroundColor: 'white' }} />
          <div>
            <div style={{ fontWeight: '200' }}>
              {settings.notesCards[2]?.title ?? '방학 중 장비 대여 안내'}
            </div>
            <div style={{ lineHeight: '1.2', fontWeight: '200', whiteSpace: 'pre-line' }}>
              {settings.notesCards[2]?.content ?? ''}
            </div>
          </div>
        </div>

        {/* 중앙 "Things Note:" 제목 */}
        <div style={{
          position: 'absolute',
          left: '60px',
          bottom: '250px',
          fontSize: '320px',
          fontWeight: 'bold',
          lineHeight: '77%'
        }}>
          Things<br />
          Note:
        </div>

        {/* 배경 이미지 디자인 - PC 버전은 유지 */}
        <div style={{
          position: 'absolute',
          right: '-50px',
          bottom: '-50px',
          width: '900px',
          height: '900px',
          opacity: '1',
          backgroundImage: `url(${process.env.PUBLIC_URL}/assets/warning.png)`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          userSelect: 'none',
          pointerEvents: 'none'
        }} />
      </div>
    </div>
  );
};

export default ThingsNotePage;
                